import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../theme';
import { useWallet, formatAddress } from '../contexts/WalletContext';
import SwapWidget from './SwapWidget';
import { useIsMobile } from '../useMediaQuery';

const SUPPORTED_CHAINS = [
  { id: 1,     name: 'Ethereum', color: '#5470D4' },
  { id: 42161, name: 'Arbitrum', color: '#1A8ED4' },
  { id: 8453,  name: 'Base',     color: '#0040D4' },
  { id: 10,    name: 'Optimism', color: '#D40010' },
  { id: 137,   name: 'Polygon',  color: '#6B35C8' },
];

/* ---------- Floating token particles ---------- */
const PARTICLE_TOKENS = [
  { symbol: 'ETH', color: '#627EEA', letter: 'E' },
  { symbol: 'USDC', color: '#2775CA', letter: 'U' },
  { symbol: 'USDT', color: '#26A17B', letter: 'T' },
  { symbol: 'DAI', color: '#F5AC37', letter: 'D' },
  { symbol: 'WBTC', color: '#F7931A', letter: 'W' },
  { symbol: 'ARB', color: '#28A0F0', letter: 'A' },
];

function useParticles(count = 14) {
  const [particles] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      token: PARTICLE_TOKENS[i % PARTICLE_TOKENS.length],
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 16 + Math.random() * 18,
      duration: 18 + Math.random() * 22,
      delay: Math.random() * -30,
      opacity: 0.08 + Math.random() * 0.10,
    }))
  );
  return particles;
}

/* ---------- Live stats data ---------- */
const SWAP_VOLUME_THRESHOLD = 10;
const AVG_SWAP_SIZE_USD = 1200;

const STATIC_STATS = [
  { color: '#0D7A4E', text: '0.5% fee -- 43% less than MetaMask' },
  { color: '#5470D4', text: 'Live on Ethereum, Arbitrum, Base, Optimism, Polygon' },
  { color: '#8A6A00', text: 'Aggregates Uniswap, Curve, Balancer and 100+ DEXs' },
  { color: '#0D7A4E', text: 'Non-custodial -- your wallet, your keys' },
  { color: '#D40010', text: 'Optimal routing finds the best price every time' },
];

/* ---------- Security badges ---------- */
const TRUST_BADGES = [
  { text: '0x Audited', iconType: 'shield', gradient: ['#0D7A4E', '#0A9B6A'] },
  { text: 'Non-custodial', iconType: 'lock', gradient: ['#5470D4', '#7090E8'] },
  { text: '100+ DEX Sources', iconType: 'bolt', gradient: ['#8A6A00', '#B8940A'] },
];

/* ---------- CSS Keyframes (injected once) ---------- */
const STYLE_ID = 'swapview-dopamine-styles';
function injectStyles(dark) {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes sv-float1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(60px, -40px) scale(1.08); }
      50% { transform: translate(-30px, -80px) scale(0.95); }
      75% { transform: translate(-60px, 20px) scale(1.05); }
    }
    @keyframes sv-float2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33% { transform: translate(-70px, 50px) scale(1.1); }
      66% { transform: translate(50px, -60px) scale(0.92); }
    }
    @keyframes sv-float3 {
      0%, 100% { transform: translate(0, 0) scale(1.02); }
      20% { transform: translate(40px, 60px) scale(0.96); }
      60% { transform: translate(-80px, -20px) scale(1.08); }
      80% { transform: translate(20px, -70px) scale(1); }
    }
    @keyframes sv-float4 {
      0%, 100% { transform: translate(0, 0) scale(0.98); }
      40% { transform: translate(-50px, -70px) scale(1.06); }
      70% { transform: translate(70px, 30px) scale(0.94); }
    }
    @keyframes sv-particle-fall {
      0% { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
      10% { opacity: 1; }
      90% { opacity: 1; }
      100% { transform: translateY(110vh) rotate(360deg); opacity: 0; }
    }
    @keyframes sv-shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes sv-stats-scroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    @keyframes sv-glow-pulse {
      0%, 100% { box-shadow: 0 0 8px rgba(0,0,0,0.15); }
      50% { box-shadow: 0 0 20px rgba(0,0,0,0.3); }
    }
    @keyframes sv-pulse-dot {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(0.8); }
    }
    @keyframes sv-bar-grow {
      from { width: 0%; }
    }
    @keyframes sv-savings-glow {
      0%, 100% { box-shadow: 0 0 8px rgba(13,122,78,0.3); }
      50% { box-shadow: 0 0 20px rgba(13,122,78,0.5), 0 0 40px rgba(13,122,78,0.15); }
    }
    @keyframes sv-badge-stagger {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

/* ========== MAIN COMPONENT ========== */
export default function SwapView({ initialSellToken, initialBuyToken }) {
  const { dark, colors } = useTheme();
  const { isConnected, account, disconnect } = useWallet();
  const isMobile = useIsMobile();
  const [selectedChain, setSelectedChain] = useState(1);
  const particles = useParticles(isMobile ? 8 : 14);
  const [swapStats, setSwapStats] = useState(null);

  useEffect(() => { injectStyles(dark); }, [dark]);

  // Fetch real swap stats
  useEffect(() => {
    fetch('/api/stats')
      .then(r => r.json())
      .then(data => setSwapStats(data))
      .catch(() => setSwapStats(null));
  }, []);

  const hasVolume = swapStats && swapStats.swaps >= SWAP_VOLUME_THRESHOLD;
  const liveStats = hasVolume
    ? [
        { color: '#D40010', text: `${swapStats.swaps.toLocaleString()} swaps today` },
        { color: '#0D7A4E', text: `$${Math.round(swapStats.swaps * AVG_SWAP_SIZE_USD / 1000)}K volume 24h` },
        { color: '#8A6A00', text: `$${Math.round(swapStats.swaps * AVG_SWAP_SIZE_USD * 0.00375).toLocaleString()} saved in fees` },
        { color: '#5470D4', text: 'Live on Ethereum, Arbitrum, Base, Optimism, Polygon' },
        { color: '#0D7A4E', text: '100+ liquidity sources' },
      ]
    : STATIC_STATS;

  const selectedChainColor = SUPPORTED_CHAINS.find(c => c.id === selectedChain)?.color || '#5470D4';

  /* ----- Gradient blob colors ----- */
  const blobColors = dark
    ? ['rgba(46,160,67,0.12)', 'rgba(30,120,200,0.10)', 'rgba(212,167,44,0.08)', 'rgba(100,60,220,0.08)']
    : ['rgba(13,122,78,0.10)', 'rgba(30,120,200,0.08)', 'rgba(138,106,0,0.06)', 'rgba(100,60,220,0.06)'];

  return (
    <div style={{
      minHeight: 'calc(100vh - 56px)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: isMobile ? '24px 16px 40px' : '40px 24px 60px',
      background: colors.bg,
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* ====== ANIMATED BACKGROUND BLOBS ====== */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        {[
          { color: blobColors[0], size: isMobile ? 280 : 420, top: '5%',  left: '10%', anim: 'sv-float1 25s ease-in-out infinite' },
          { color: blobColors[1], size: isMobile ? 260 : 380, top: '30%', right: '5%', anim: 'sv-float2 30s ease-in-out infinite' },
          { color: blobColors[2], size: isMobile ? 220 : 340, bottom: '10%', left: '20%', anim: 'sv-float3 28s ease-in-out infinite' },
          { color: blobColors[3], size: isMobile ? 200 : 300, top: '60%', right: '25%', anim: 'sv-float4 32s ease-in-out infinite' },
        ].map((blob, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: blob.size, height: blob.size,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${blob.color} 0%, transparent 70%)`,
            filter: 'blur(60px)',
            top: blob.top, left: blob.left, right: blob.right, bottom: blob.bottom,
            animation: blob.anim,
            willChange: 'transform',
          }} />
        ))}
      </div>

      {/* ====== TOKEN RAIN PARTICLES ====== */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        {particles.map(p => (
          <div key={p.id} style={{
            position: 'absolute',
            left: `${p.x}%`,
            width: p.size, height: p.size,
            borderRadius: '50%',
            background: p.token.color,
            opacity: p.opacity,
            filter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: p.size * 0.45,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.6)',
            animation: `sv-particle-fall ${p.duration}s linear ${p.delay}s infinite`,
            willChange: 'transform, opacity',
          }}>
            {p.token.letter}
          </div>
        ))}
      </div>

      {/* ====== CONTENT (above blobs) ====== */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>

        {/* ====== LIVE STATS TICKER ====== */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            width: '100%', maxWidth: 600, overflow: 'hidden', marginBottom: 28,
            borderRadius: 10,
            background: dark ? 'rgba(22,27,34,0.7)' : 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: `1px solid ${colors.border}`,
            padding: '8px 0',
          }}
        >
          <div style={{
            display: 'flex', gap: 40, whiteSpace: 'nowrap',
            animation: 'sv-stats-scroll 20s linear infinite',
            width: 'max-content',
          }}>
            {/* Duplicate for seamless loop */}
            {[...liveStats, ...liveStats].map((stat, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 500,
                color: colors.textSecondary,
              }}>
                <span style={{
                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                  background: stat.color, flexShrink: 0,
                }} />
                <span>{stat.text}</span>
                <span style={{
                  display: 'inline-block', width: 4, height: 4, borderRadius: '50%',
                  background: colors.green,
                  animation: 'sv-pulse-dot 2s ease-in-out infinite',
                  marginLeft: 4,
                }} />
              </div>
            ))}
          </div>
        </motion.div>

        {/* ====== ANIMATED HERO TEXT ====== */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ textAlign: 'center', marginBottom: 28 }}
        >
          <h1 style={{
            fontFamily: "'DM Serif Display', serif", fontSize: isMobile ? 30 : 40,
            margin: 0, lineHeight: 1.2,
            background: dark
              ? 'linear-gradient(90deg, #2EA043 0%, #D4A72C 50%, #2EA043 100%)'
              : 'linear-gradient(90deg, #0D7A4E 0%, #8A6A00 50%, #0D7A4E 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'sv-shimmer 4s linear infinite',
          }}>
            Swap at the best rate
          </h1>
          <p style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
            color: colors.textSecondary, marginTop: 10, maxWidth: 420, lineHeight: 1.6,
          }}>
            Trade tokens across 100+ liquidity sources with just{' '}
            <span style={{ color: colors.green, fontWeight: 600 }}>0.5% fee</span>
            {' '}&mdash; 43% less than MetaMask.
          </p>
        </motion.div>

        {/* ====== CHAIN SELECTOR with glow ====== */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.3 }}
          style={{
            display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap', justifyContent: 'center',
          }}
        >
          {SUPPORTED_CHAINS.map(ch => {
            const active = selectedChain === ch.id;
            return (
              <motion.button
                key={ch.id}
                onClick={() => setSelectedChain(ch.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: active ? 600 : 500,
                  color: active ? '#FFFFFF' : colors.textSecondary,
                  background: active ? ch.color : 'transparent',
                  border: active ? 'none' : `1px solid ${colors.border}`,
                  borderRadius: 20, padding: isMobile ? '10px 16px' : '5px 14px', cursor: 'pointer',
                  transition: 'all 0.2s ease', letterSpacing: 0.3,
                  position: 'relative',
                  minHeight: isMobile ? 44 : 'auto',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  boxShadow: active ? `0 4px 20px ${ch.color}44, 0 0 0 1px ${ch.color}33` : 'none',
                }}
                onMouseEnter={e => {
                  if (!active) {
                    e.currentTarget.style.background = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                    e.currentTarget.style.color = colors.textPrimary;
                  }
                }}
                onMouseLeave={e => {
                  if (!active) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = colors.textSecondary;
                  }
                }}
              >
                {ch.name}
              </motion.button>
            );
          })}
        </motion.div>

        {/* ====== WALLET INFO ====== */}
        {isConnected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            }}
          >
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: colors.green,
            }} />
            <span style={{ color: colors.textSecondary }}>{formatAddress(account)}</span>
            <button
              onClick={disconnect}
              style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: isMobile ? 12 : 10, fontWeight: 600,
                color: colors.red, background: 'transparent', border: 'none',
                cursor: 'pointer', padding: isMobile ? '10px 12px' : '2px 4px',
                minHeight: isMobile ? 44 : 'auto',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Disconnect
            </button>
          </motion.div>
        )}

        {/* ====== SWAP WIDGET (with glass card wrapper) ====== */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          style={{ position: 'relative' }}
        >
          {/* Subtle ring glow behind widget */}
          <div style={{
            position: 'absolute', top: -8, left: -8, right: -8, bottom: -8,
            borderRadius: 20,
            background: `linear-gradient(135deg, ${selectedChainColor}22, ${colors.green}18, ${colors.gold}15)`,
            filter: 'blur(16px)',
            zIndex: -1,
          }} />
          <SwapWidget
            chainId={selectedChain}
            initialSellToken={initialSellToken}
            initialBuyToken={initialBuyToken}
          />
        </motion.div>

        {/* ====== FEE COMPARISON — Savings Calculator ====== */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          style={{
            marginTop: 28, maxWidth: 420, width: '100%',
            background: dark ? 'rgba(18,38,26,0.6)' : 'rgba(232,248,242,0.85)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: `1px solid ${colors.greenBorder}`,
            borderRadius: 14, padding: '20px 22px',
            boxShadow: dark
              ? '0 0 30px rgba(46,160,67,0.12), 0 2px 8px rgba(0,0,0,0.2)'
              : '0 0 30px rgba(13,122,78,0.08), 0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          {/* Header row with title + savings badge */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 18,
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 600,
              color: colors.green, letterSpacing: 1.2, textTransform: 'uppercase',
            }}>
              Fee Comparison
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 0.4, type: 'spring', stiffness: 200 }}
              style={{
                fontFamily: "'DM Serif Display', serif", fontSize: 14, fontWeight: 400,
                color: '#FFFFFF',
                background: dark
                  ? 'linear-gradient(135deg, #2EA043 0%, #0D7A4E 100%)'
                  : 'linear-gradient(135deg, #0D7A4E 0%, #0A9B6A 100%)',
                padding: '5px 14px',
                borderRadius: 20,
                animation: 'sv-savings-glow 2.5s ease-in-out infinite',
                letterSpacing: 0.3,
              }}
            >
              YOU SAVE 43%
            </motion.div>
          </div>

          {/* Fee bars */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { name: 'PoolScope', fee: '0.5%', pct: 50, highlight: true, note: 'Aggregates 100+ DEXs for the best net price' },
              { name: 'Jupiter', fee: '0.6%', pct: 60, highlight: false, note: null },
              { name: 'MetaMask Swaps', fee: '0.875%', pct: 87.5, highlight: false, note: null },
              { name: 'Coinbase Wallet', fee: '1.0%', pct: 100, highlight: false, note: null },
            ].map((item, idx) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + idx * 0.08, duration: 0.35 }}
              >
                {/* Label row */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 5,
                }}>
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                    fontWeight: item.highlight ? 600 : 400,
                    color: item.highlight ? colors.green : (dark ? 'rgba(139,148,158,0.5)' : 'rgba(102,102,102,0.5)'),
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    {item.highlight && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 16, height: 16, borderRadius: '50%',
                        background: dark ? 'rgba(46,160,67,0.2)' : 'rgba(13,122,78,0.12)',
                        fontSize: 10, color: colors.green,
                      }}>
                        {'\u2713'}
                      </span>
                    )}
                    {item.name}
                    {item.dimmed && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 400,
                        color: dark ? 'rgba(139,148,158,0.4)' : 'rgba(153,153,153,0.7)',
                        marginLeft: 2,
                      }}>
                        (own liquidity only)
                      </span>
                    )}
                    {item.highlight && (
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 500,
                        color: colors.green, marginLeft: 2,
                        opacity: 0.8,
                      }}>
                        BEST VALUE
                      </span>
                    )}
                  </span>
                  <span style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: item.highlight ? 16 : 13,
                    fontWeight: 400,
                    color: item.highlight ? colors.green : (dark ? 'rgba(139,148,158,0.35)' : 'rgba(170,170,170,0.7)'),
                    textDecoration: item.highlight ? 'none' : 'line-through',
                    textDecorationColor: dark ? 'rgba(248,81,73,0.4)' : 'rgba(184,0,26,0.3)',
                  }}>
                    {item.fee}
                  </span>
                </div>
                {/* Visual bar */}
                <div style={{
                  width: '100%', height: item.highlight ? 10 : 7,
                  borderRadius: 5,
                  background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${item.pct}%` }}
                    transition={{ delay: 0.6 + idx * 0.12, duration: 0.7, ease: 'easeOut' }}
                    style={{
                      height: '100%',
                      borderRadius: 5,
                      background: item.highlight
                        ? (dark
                          ? 'linear-gradient(90deg, #2EA043, #3FD068)'
                          : 'linear-gradient(90deg, #0D7A4E, #0A9B6A)')
                        : (dark
                          ? 'linear-gradient(90deg, rgba(248,81,73,0.25), rgba(248,81,73,0.15))'
                          : 'linear-gradient(90deg, rgba(184,0,26,0.18), rgba(184,0,26,0.08))'),
                      boxShadow: item.highlight
                        ? (dark ? '0 0 12px rgba(46,160,67,0.3)' : '0 0 12px rgba(13,122,78,0.2)')
                        : 'none',
                    }}
                  />
                </div>
                {/* Note under PoolScope */}
                {item.note && (
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fontWeight: 500,
                    color: dark ? 'rgba(46,160,67,0.7)' : 'rgba(13,122,78,0.6)',
                    marginTop: 4, paddingLeft: 21,
                    letterSpacing: 0.3,
                  }}>
                    {item.note}
                  </div>
                )}
              </motion.div>
            ))}
          </div>

        </motion.div>

        {/* ====== SECURITY / TRUST BADGES ====== */}
        <div style={{
          marginTop: 20, maxWidth: 420, width: '100%',
          display: 'flex', flexDirection: 'row', gap: 8,
          justifyContent: 'center', flexWrap: 'wrap',
        }}>
          {TRUST_BADGES.map((badge, idx) => (
            <motion.div
              key={badge.text}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + idx * 0.12, duration: 0.4, type: 'spring', stiffness: 160 }}
              whileHover={{ y: -3, boxShadow: dark
                ? `0 8px 24px rgba(0,0,0,0.3), 0 0 16px ${badge.gradient[0]}33`
                : `0 8px 24px rgba(0,0,0,0.08), 0 0 16px ${badge.gradient[0]}22`
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 600,
                color: dark ? '#C9D1D9' : '#444444',
                padding: '8px 14px',
                background: dark
                  ? `linear-gradient(135deg, ${badge.gradient[0]}18, ${badge.gradient[1]}10)`
                  : `linear-gradient(135deg, ${badge.gradient[0]}0D, ${badge.gradient[1]}08)`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: `1px solid ${dark ? badge.gradient[0] + '30' : badge.gradient[0] + '20'}`,
                borderRadius: 20,
                cursor: 'default',
                letterSpacing: 0.4,
                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                boxShadow: dark
                  ? `0 2px 8px rgba(0,0,0,0.15), 0 0 0 0 ${badge.gradient[0]}00`
                  : `0 2px 8px rgba(0,0,0,0.04), 0 0 0 0 ${badge.gradient[0]}00`,
              }}
            >
              {/* CSS icon shapes */}
              {badge.iconType === 'shield' && (
                <span style={{
                  display: 'inline-block', width: 14, height: 16, flexShrink: 0,
                  position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute', top: 0, left: 0, width: 14, height: 10,
                    borderRadius: '2px 2px 0 0',
                    background: `linear-gradient(135deg, ${badge.gradient[0]}, ${badge.gradient[1]})`,
                  }} />
                  <span style={{
                    position: 'absolute', top: 10, left: 0,
                    width: 0, height: 0,
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: `6px solid ${badge.gradient[1]}`,
                  }} />
                  <span style={{
                    position: 'absolute', top: 3, left: 4.5, width: 5, height: 3,
                    borderBottom: `1.5px solid #FFFFFF`,
                    borderLeft: `1.5px solid #FFFFFF`,
                    transform: 'rotate(-45deg)',
                    opacity: 0.9,
                  }} />
                </span>
              )}
              {badge.iconType === 'lock' && (
                <span style={{
                  display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                  width: 14, flexShrink: 0,
                }}>
                  <span style={{
                    width: 8, height: 5,
                    borderRadius: '4px 4px 0 0',
                    border: `2px solid ${badge.gradient[0]}`,
                    borderBottom: 'none',
                    boxSizing: 'border-box',
                  }} />
                  <span style={{
                    width: 12, height: 8,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${badge.gradient[0]}, ${badge.gradient[1]})`,
                  }} />
                </span>
              )}
              {badge.iconType === 'bolt' && (
                <span style={{
                  display: 'inline-block', width: 12, height: 16, flexShrink: 0,
                  position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute', top: 0, left: 2,
                    width: 0, height: 0,
                    borderLeft: '3px solid transparent',
                    borderRight: '5px solid transparent',
                    borderBottom: `9px solid ${badge.gradient[0]}`,
                    transform: 'rotate(10deg)',
                  }} />
                  <span style={{
                    position: 'absolute', top: 7, left: 4,
                    width: 0, height: 0,
                    borderLeft: '5px solid transparent',
                    borderRight: '3px solid transparent',
                    borderTop: `9px solid ${badge.gradient[1]}`,
                    transform: 'rotate(10deg)',
                  }} />
                </span>
              )}
              <span>{badge.text}</span>
            </motion.div>
          ))}
        </div>

      </div>
    </div>
  );
}
